import Axios from "axios";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Store } from "../../Store";
import { getError } from "../../utils";
import { api } from "../../Api";
import ImageCropModal from "../../component/ImageCropModal";

const DEFAULT_LOGO =
  "https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png";

const fmt = (n) => Number(n || 0).toLocaleString();

function Avatar({ src, size = 44 }) {
  return (
    <img
      src={src || DEFAULT_LOGO}
      alt=""
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid #fff",
        boxShadow: "0 1px 4px rgba(0,0,0,.15)",
        background: "#f1f3f5",
        flexShrink: 0,
      }}
    />
  );
}

function Tile({ icon, label, value, sub, tone }) {
  return (
    <div className={`assoc-tile assoc-tile-${tone}`}>
      <i className={`fa ${icon}`} aria-hidden="true" />
      <div>
        <div className="assoc-tile-value">{value}</div>
        <div className="assoc-tile-label">{label}</div>
        {sub ? <div className="assoc-tile-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

const EMPTY_LIST = { rows: [], page: 1, pages: 1, count: 0, summary: null };

/**
 * MERCHANTS (associations in code) — the admin's list of every merchant with
 * their transfer / withdraw statistics, plus add, edit, enable/disable and
 * remove. A merchant can send their shopping (topup) balance to their own
 * downline and pay out withdraws members route to them (see
 * AssociationTransferScreen for the transfer ledger).
 *
 * Remove is only offered while a merchant has no history — once they have
 * sent a transfer or had a withdraw routed to them, the server refuses and
 * disabling is the way to pause them.
 */
export default function AssociationScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = { headers: { authorization: `Bearer ${userInfo.token}` } };

  /* ------------------------------------------------------------- list */

  const [list, setList] = useState(EMPTY_LIST);
  const [listLoading, setListLoading] = useState(false);
  const [listQuery, setListQuery] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "all", pending: false, sort: "name" });
  const [busyId, setBusyId] = useState("");

  const loadList = useCallback(
    async (f = filters, page = 1) => {
      setListLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize: 12, status: f.status, sort: f.sort });
        if (f.q) params.set("q", f.q);
        if (f.pending) params.set("pending", "1");
        const { data } = await Axios.get(api + `/api/association/admin/list?${params.toString()}`, auth);
        setList(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setListLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    loadList(next, 1);
  };

  const reload = () => loadList(filters, list.page);

  /* ------------------------------------------------------- add / edit */

  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const [selected, setSelected] = useState(null); // the user being promoted/edited
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const openAdd = () => {
    setSelected(null);
    setName("");
    setLogoUrl("");
    setQuery("");
    setSearchResults([]);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    pickUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || uploading) return;
    setModalOpen(false);
  };

  const runSearch = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await Axios.get(
          api + `/api/association/admin/search-users?q=${encodeURIComponent(value.trim())}`,
          auth,
        );
        setSearchResults(data.rows || []);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const pickUser = (user) => {
    setSelected(user);
    setName(user.associationName || "");
    setLogoUrl(user.associationLogo || "");
    setSearchResults([]);
    setQuery("");
  };

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = "";
  };

  const onCropDone = async (blob) => {
    setPendingFile(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", blob, "association-logo.jpg");
      const { data } = await Axios.post(api + "/api/upload/", form, {
        headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.secure_url);
      toast.success("Logo uploaded.");
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!selected) return;
    if (!name.trim()) {
      toast.error("Give this merchant a name.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await Axios.post(
        api + "/api/association/admin/save",
        { userId: selected._id, name: name.trim(), logo: logoUrl },
        auth,
      );
      toast.success(data.message);
      setModalOpen(false);
      setSelected(null);
      reload();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------- status / remove */

  const toggleStatus = async (u) => {
    const disable = !u.associationDisabled;
    const prompt = disable
      ? `Disable ${u.associationName}? Members will no longer be able to pick them for withdraws, and they cannot send transfers or mark withdraws paid until enabled again.`
      : `Enable ${u.associationName} again?`;
    if (!window.confirm(prompt)) return;
    setBusyId(u._id);
    try {
      const { data } = await Axios.post(
        api + "/api/association/admin/status",
        { userId: u._id, disabled: disable },
        auth,
      );
      toast.success(data.message);
      reload();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusyId("");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.associationName}? @${u.username} stays a normal member.`)) return;
    setBusyId(u._id);
    try {
      const { data } = await Axios.post(api + "/api/association/admin/remove", { userId: u._id }, auth);
      toast.success(data.message);
      reload();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusyId("");
    }
  };

  const s = list.summary;

  return (
    <div className="assoc-screen">
      <Helmet>
        <title>Merchants</title>
      </Helmet>
      <style>{CSS}</style>

      <div className="assoc-hero">
        <div>
          <h3 className="mb-1">
            <i className="fa fa-handshake-o me-2" aria-hidden="true" /> Merchants
          </h3>
          <p className="mb-0 small">
            Merchants share their shopping balance with their downline and pay out withdraws routed to them.
          </p>
        </div>
        <Button variant="light" className="assoc-add-btn" onClick={openAdd}>
          <i className="fa fa-plus me-1" aria-hidden="true" /> Add New Merchant
        </Button>
      </div>

      {s ? (
        <div className="assoc-tiles">
          <Tile
            icon="fa-users"
            tone="blue"
            label="Merchants"
            value={fmt(s.merchants)}
            sub={`${fmt(s.enabled)} enabled · ${fmt(s.disabled)} disabled`}
          />
          <Tile
            icon="fa-exchange"
            tone="purple"
            label="Total transferred"
            value={fmt(s.transferAmount)}
            sub={`${fmt(s.transferCount)} transfers`}
          />
          <Tile
            icon="fa-hourglass-half"
            tone="amber"
            label="Pending withdraws"
            value={fmt(s.pendingAmount)}
            sub={`${fmt(s.pendingCount)} requests`}
          />
          <Tile
            icon="fa-check-circle"
            tone="green"
            label="Paid withdraws"
            value={fmt(s.paidAmount)}
            sub={`${fmt(s.paidCount)} requests`}
          />
        </div>
      ) : null}

      <Card className="assoc-card">
        <Card.Body>
          <div className="assoc-toolbar">
            <Form.Control
              size="sm"
              className="assoc-search"
              placeholder="Search name, username or phone…"
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters({ q: listQuery.trim() })}
            />
            <Form.Select
              size="sm"
              className="assoc-select"
              value={filters.status}
              onChange={(e) => applyFilters({ status: e.target.value })}
            >
              <option value="all">All statuses</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </Form.Select>
            <Form.Select
              size="sm"
              className="assoc-select"
              value={filters.sort}
              onChange={(e) => applyFilters({ sort: e.target.value })}
            >
              <option value="name">Sort: Name</option>
              <option value="newest">Sort: Newest</option>
              <option value="transferred">Sort: Most transferred</option>
              <option value="pending">Sort: Most pending</option>
              <option value="paid">Sort: Most paid</option>
              <option value="balance">Sort: Shopping balance</option>
            </Form.Select>
            <Form.Check
              type="switch"
              id="assoc-pending-only"
              label="Has pending withdraws"
              className="small mb-0"
              checked={filters.pending}
              onChange={(e) => applyFilters({ pending: e.target.checked })}
            />
            <Button size="sm" variant="primary" onClick={() => applyFilters({ q: listQuery.trim() })}>
              <i className="fa fa-search" aria-hidden="true" />
            </Button>
          </div>

          <div className="small text-muted mb-2">{fmt(list.count)} merchant(s)</div>

          {listLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : list.rows.length === 0 ? (
            <div className="text-muted small py-4 text-center">
              {filters.q || filters.status !== "all" || filters.pending
                ? "No merchants match these filters."
                : "No merchants yet. Use “Add New Merchant” to create one."}
            </div>
          ) : (
            <div className="assoc-grid">
              {list.rows.map((u) => {
                const st = u.stats || {};
                const busy = busyId === u._id;
                return (
                  <div key={u._id} className={u.associationDisabled ? "assoc-m assoc-m-off" : "assoc-m"}>
                    <div className="assoc-m-head">
                      <Avatar src={u.associationLogo || u.profilePhoto} size={52} />
                      <div className="assoc-m-id">
                        <div className="assoc-m-name">{u.associationName}</div>
                        <div className="small text-muted text-truncate">
                          {u.name} · @{u.username}
                        </div>
                        {u.phone ? <div className="small text-muted">{u.phone}</div> : null}
                      </div>
                      <Badge bg={u.associationDisabled ? "secondary" : "success"} className="assoc-m-badge">
                        {u.associationDisabled ? "Disabled" : "Enabled"}
                      </Badge>
                    </div>

                    <div className="assoc-m-stats">
                      <div>
                        <span className="assoc-m-num">{fmt(st.transferAmount)}</span>
                        <span className="assoc-m-lbl">Sent · {fmt(st.transferCount)}</span>
                      </div>
                      <div>
                        <span className="assoc-m-num text-warning">{fmt(st.pendingAmount)}</span>
                        <span className="assoc-m-lbl">Pending · {fmt(st.pendingCount)}</span>
                      </div>
                      <div>
                        <span className="assoc-m-num text-success">{fmt(st.paidAmount)}</span>
                        <span className="assoc-m-lbl">Paid · {fmt(st.paidCount)}</span>
                      </div>
                      <div>
                        <span className="assoc-m-num">{fmt(u.shoppingbalance)}</span>
                        <span className="assoc-m-lbl">Shopping bal.</span>
                      </div>
                    </div>

                    <div className="assoc-m-actions">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(u)} disabled={busy}>
                        <i className="fa fa-pencil me-1" aria-hidden="true" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={u.associationDisabled ? "outline-success" : "outline-warning"}
                        onClick={() => toggleStatus(u)}
                        disabled={busy}
                      >
                        <i className={`fa ${u.associationDisabled ? "fa-play" : "fa-pause"} me-1`} aria-hidden="true" />
                        {u.associationDisabled ? "Enable" : "Disable"}
                      </Button>
                      {u.hasHistory ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`rm-${u._id}`}>Has history — disable instead</Tooltip>}
                        >
                          {/* Disabled buttons swallow pointer events, so the tooltip hangs off a wrapper. */}
                          <span className="d-inline-block" tabIndex={0}>
                            <Button size="sm" variant="outline-danger" disabled style={{ pointerEvents: "none" }}>
                              <i className="fa fa-trash me-1" aria-hidden="true" />
                              Remove
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <Button size="sm" variant="outline-danger" onClick={() => remove(u)} disabled={busy}>
                          <i className="fa fa-trash me-1" aria-hidden="true" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {list.pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={list.page <= 1}
                onClick={() => loadList(filters, list.page - 1)}
              >
                Prev
              </Button>
              <span className="small align-self-center">
                Page {list.page} / {list.pages}
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={list.page >= list.pages}
                onClick={() => loadList(filters, list.page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            {selected?.isAssociation ? "Edit merchant" : "Add new merchant"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!selected ? (
            <>
              <Form.Label className="small">Find a member</Form.Label>
              <Form.Control
                autoFocus
                placeholder="Search by username, name or phone…"
                value={query}
                onChange={(e) => runSearch(e.target.value)}
              />
              {searching && <div className="small text-muted mt-2">Searching…</div>}
              {searchResults.length > 0 && (
                <div className="assoc-results mt-2">
                  {searchResults.map((u) => (
                    <button key={u._id} type="button" className="assoc-result-row" onClick={() => pickUser(u)}>
                      <Avatar src={u.profilePhoto} size={36} />
                      <div className="flex-grow-1 text-start">
                        <div className="fw-semibold">{u.name}</div>
                        <div className="small text-muted">
                          @{u.username}
                          {u.phone ? ` · ${u.phone}` : ""}
                        </div>
                      </div>
                      {u.isAssociation && <Badge bg="success">{u.associationName || "Merchant"}</Badge>}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Avatar src={selected.profilePhoto} size={48} />
                <div className="flex-grow-1">
                  <div className="fw-semibold">{selected.name}</div>
                  <div className="small text-muted">@{selected.username}</div>
                </div>
                {!selected.isAssociation && (
                  <Button size="sm" variant="link" onClick={() => setSelected(null)}>
                    Change
                  </Button>
                )}
              </div>

              <div className="d-flex align-items-center gap-3 mb-3">
                <Avatar src={logoUrl} size={72} />
                <div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Spinner size="sm" animation="border" /> : "Choose logo"}
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileChosen} />
                  <div className="small text-muted mt-1">Drag to position, then crop to a perfect square.</div>
                </div>
              </div>

              <Form.Group>
                <Form.Label className="small">Merchant name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Dhaka Traders Merchant"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" onClick={save} disabled={!selected || saving || uploading}>
            {saving ? "Saving…" : selected?.isAssociation ? "Save changes" : "Make Merchant"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ImageCropModal
        show={Boolean(pendingFile)}
        file={pendingFile}
        title="Crop merchant logo"
        onCancel={() => setPendingFile(null)}
        onDone={onCropDone}
      />
    </div>
  );
}

const CSS = `
.assoc-screen { padding: 16px; max-width: 1200px; margin: 0 auto; }
.assoc-hero {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
}
.assoc-add-btn { font-weight: 600; color: #4b2bd1; }
.assoc-card { border-radius: 14px; border: none; box-shadow: 0 2px 10px rgba(0,0,0,.06); }

.assoc-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.assoc-tile {
  display: flex; align-items: center; gap: 12px; background: #fff; border-radius: 14px;
  padding: 14px 16px; box-shadow: 0 2px 10px rgba(0,0,0,.06); min-width: 0;
}
.assoc-tile > i {
  width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.assoc-tile-blue > i { background: #e7f0ff; color: #2563eb; }
.assoc-tile-purple > i { background: #f1e8ff; color: #7c3aed; }
.assoc-tile-amber > i { background: #fef3c7; color: #b45309; }
.assoc-tile-green > i { background: #dcfce7; color: #15803d; }
.assoc-tile-value { font-size: 20px; font-weight: 800; line-height: 1.1; word-break: break-word; }
.assoc-tile-label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: .03em; }
.assoc-tile-sub { font-size: 12px; color: #94a3b8; }

.assoc-toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.assoc-search { flex: 1 1 220px; min-width: 0; }
.assoc-select { width: auto; flex: 0 1 190px; }

.assoc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
.assoc-m {
  border: 1.5px solid #eef0f4; border-radius: 14px; padding: 14px; background: #fff;
  display: flex; flex-direction: column; gap: 12px; min-width: 0;
}
.assoc-m-off { background: #f8f9fa; }
.assoc-m-off .assoc-m-head img { filter: grayscale(1); opacity: .7; }
.assoc-m-head { display: flex; align-items: center; gap: 12px; min-width: 0; }
.assoc-m-id { flex: 1; min-width: 0; }
.assoc-m-name { font-weight: 700; font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.assoc-m-badge { align-self: flex-start; }
.assoc-m-stats {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px;
  background: #f8fafc; border-radius: 10px; padding: 10px 8px; text-align: center;
}
.assoc-m-stats > div { display: flex; flex-direction: column; min-width: 0; }
.assoc-m-num { font-weight: 800; font-size: 15px; word-break: break-word; }
.assoc-m-lbl { font-size: 11px; color: #64748b; }
.assoc-m-actions { display: flex; gap: 6px; flex-wrap: wrap; }

.assoc-results { max-height: 300px; overflow-y: auto; border: 1px solid #eee; border-radius: 10px; }
.assoc-result-row {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 10px 12px; border: none; background: #fff; border-bottom: 1px solid #f2f2f2;
  cursor: pointer; text-align: left;
}
.assoc-result-row:hover { background: #f8f9ff; }
.assoc-result-row:last-child { border-bottom: none; }

@media (max-width: 992px) {
  .assoc-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 576px) {
  .assoc-screen { padding: 10px; }
  .assoc-hero { padding: 16px; }
  .assoc-add-btn { width: 100%; }
  .assoc-tiles { gap: 8px; }
  .assoc-tile { padding: 10px 12px; gap: 8px; }
  .assoc-tile > i { width: 34px; height: 34px; font-size: 15px; }
  .assoc-tile-value { font-size: 16px; }
  .assoc-grid { grid-template-columns: 1fr; }
  .assoc-select { flex: 1 1 45%; }
  .assoc-m-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 10px; }
  .assoc-m-actions .btn, .assoc-m-actions > span { flex: 1 1 auto; }
  .assoc-m-actions > span .btn { width: 100%; }
}
`;
